import { apiClient } from '../utils/api';

export type ClientUploadStatus = 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED';
export type ClientUploadType = 'IDENTITY_CARD' | 'MEDICAL_DOCUMENT' | 'OTHER';
export type ClientDocumentSignatureStatus =
  | 'NOT_APPLICABLE'
  | 'NOT_REQUIRED'
  | 'PENDING'
  | 'PARTIAL'
  | 'SIGNED';

export type AdminClientDocumentRow =
  | {
      source: 'uploaded';
      id: string;
      clientId: string;
      documentType: ClientUploadType;
      status: ClientUploadStatus;
      displayName?: string | null;
      descriptionHtml?: string | null;
      originalFileName: string;
      mimeType: string;
      sizeBytes: number;
      createdAt: string;
      reviewedAt?: string | null;
      reviewedByUserId?: string | null;
      reviewComment?: string | null;
      fileUrl: string;
      kind: string;
      requiresSignature: boolean;
      signatureStatus: ClientDocumentSignatureStatus;
      requiredSigners: string[];
      signedRoles: string[];
      fileExists: boolean;
      client?: {
        id: string;
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
        phone?: string | null;
      };
    }
  | {
      source: 'generated';
      id: string;
      entityType: string;
      entityId: string;
      templateCode: string;
      createdAt: string;
      fileUrl: string;
      kind: string;
      requiresSignature: boolean;
      signatureStatus: ClientDocumentSignatureStatus;
      requiredSigners: string[];
      signedRoles: string[];
      fileExists: boolean;
      client?: {
        id?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
        phone?: string | null;
      };
    };

async function parseJsonSafe(res: Response): Promise<any> {
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) return null;
  return await res.json();
}

export class ClientUploadsApi {
  async list(params?: {
    page?: number;
    limit?: number;
    status?: ClientUploadStatus | '';
    source?: 'uploaded' | 'generated' | '';
    /** Generated documents only: APPOINTMENT | CONTRACT | INVOICE */
    entityType?: 'APPOINTMENT' | 'CONTRACT' | 'INVOICE' | '';
    search?: string;
    from?: string; // YYYY-MM-DD
    to?: string;   // YYYY-MM-DD
  }) {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.status) qs.set('status', params.status);
    if (params?.source) qs.set('source', params.source);
    if (params?.entityType) qs.set('entityType', params.entityType);
    if (params?.search) qs.set('search', params.search);
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';

    const res = await apiClient(`/api/admin/client-documents${suffix}`, { method: 'GET' }, true);
    const data = await parseJsonSafe(res);
    if (!res.ok) throw new Error(data?.message || 'Failed to list client uploads');
    return data as { success: boolean; data: AdminClientDocumentRow[]; total: number; page: number; limit: number };
  }

  async review(id: string, body: { status: 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED'; reviewComment?: string }) {
    const res = await apiClient(
      `/api/admin/client-uploads/${encodeURIComponent(id)}/review`,
      { method: 'PATCH', body: JSON.stringify(body) },
      true,
    );
    const data = await parseJsonSafe(res);
    if (!res.ok) throw new Error(data?.message || 'Failed to review upload');
    return data;
  }

  async fetchFileBlob(id: string): Promise<{ blob: Blob; mimeType?: string | null; fileName?: string }> {
    const res = await apiClient(`/api/admin/client-uploads/${encodeURIComponent(id)}/file`, { method: 'GET' }, true);
    if (!res.ok) {
      const data = await parseJsonSafe(res);
      throw new Error(data?.message || 'Failed to fetch file');
    }
    const blob = await res.blob();
    const mimeType = res.headers.get('content-type');
    const disposition = res.headers.get('content-disposition') || '';
    const fileName = disposition.includes('filename=')
      ? decodeURIComponent(disposition.split('filename=')[1].replace(/\"/g, '').trim())
      : undefined;
    return { blob, mimeType, fileName };
  }

  async delete(id: string): Promise<{ success: boolean; data?: { id: string; fileDeleted: boolean } }> {
    const res = await apiClient(`/api/admin/client-uploads/${encodeURIComponent(id)}`, { method: 'DELETE' }, true);
    const data = await parseJsonSafe(res);
    if (!res.ok) throw new Error(data?.message || 'Failed to delete uploaded document');
    return data;
  }

  async deleteGenerated(ids: string[]): Promise<any> {
    const res = await apiClient(`/api/documents`, { method: 'DELETE', body: JSON.stringify({ ids }) }, true);
    const data = await parseJsonSafe(res);
    if (!res.ok && res.status !== 207) throw new Error(data?.message || 'Failed to delete generated document');
    return data;
  }

  async fetchBlobByUrl(url: string): Promise<{ blob: Blob; mimeType?: string | null }> {
    const res = await apiClient(url, { method: 'GET' }, true);
    if (!res.ok) {
      const data = await parseJsonSafe(res);
      throw new Error(data?.message || 'Failed to fetch file');
    }
    const blob = await res.blob();
    const mimeType = res.headers.get('content-type');
    return { blob, mimeType };
  }
}

export const clientUploadsApi = new ClientUploadsApi();


